import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useCompactViewport } from '~/hooks/useCompactViewport'
import { formatDisplayDate, parseApiDate } from '~/utils/timezone'
import { cm } from '~/utils/utils'
import { MonthCalendar, isDateInSelectableRange } from './MonthCalendar'
import { Typography } from './Typography'

// ============================================================================
// Icons
// ============================================================================

const CalendarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='24'
    height='24'
    viewBox='0 0 24 24'
    fill='none'
    className={className}
  >
    <rect x='3' y='4' width='18' height='18' rx='2' stroke='currentColor' strokeWidth='1.5' />
    <path d='M16 2V6M8 2V6M3 10H21' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' />
  </svg>
)

// ============================================================================
// Helpers
// ============================================================================

function toApiDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function isValidYmd(s: string | undefined | null): s is string {
  if (!s || s.length !== 10) return false
  const d = parseApiDate(s)
  return d != null && !isNaN(d.getTime())
}

function clampDateString(value: string, min?: string, max?: string): string {
  if (!isValidYmd(value)) return value
  if (min && value < min) return min
  if (max && value > max) return max
  return value
}

// ============================================================================
// Types
// ============================================================================

export interface DatePickerPopoverProps {
  /** YYYY-MM-DD selected value, or '' for none. */
  value: string
  /** Called with the new YYYY-MM-DD value (or '' when cleared). */
  onChange: (value: string) => void

  /** Inclusive YYYY-MM-DD lower bound. */
  min?: string
  /** Inclusive YYYY-MM-DD upper bound. */
  max?: string

  /** Placeholder text shown when no date is selected. */
  placeholder?: string

  /** Disable interaction. */
  isDisabled?: boolean

  /** Show the "Clear" footer action (default true). */
  allowClear?: boolean
  /** Show the "Today" footer action (default true). */
  allowToday?: boolean

  /** Extra className for the trigger button. */
  className?: string

  /** ARIA label for the trigger. */
  'aria-label'?: string
  /** Test id for the trigger. */
  'data-testid'?: string
}

// ============================================================================
// Component
// ============================================================================

/**
 * Single-date popover styled like the billing-period date picker.
 *
 * Composed from {@link MonthCalendar} so the look-and-feel stays in sync with
 * `DateRangePanel` (the dual-calendar billing-period picker).
 */
export const DatePickerPopover: React.FC<DatePickerPopoverProps> = ({
  value,
  onChange,
  min,
  max,
  placeholder = 'Select date',
  isDisabled = false,
  allowClear = true,
  allowToday = true,
  className,
  'aria-label': ariaLabel,
  'data-testid': testId
}) => {
  const isCompactViewport = useCompactViewport()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 })

  const selectedDate = useMemo(() => (isValidYmd(value) ? parseApiDate(value) : null), [value])

  const anchor = useMemo(() => selectedDate ?? new Date(), [selectedDate])
  const [viewYear, setViewYear] = useState(() => anchor.getFullYear())
  const [viewMonth, setViewMonth] = useState(() => anchor.getMonth())

  // Re-anchor the visible month when the popover opens so the user lands on
  // the currently-selected date instead of wherever they last navigated to.
  useEffect(() => {
    if (!isOpen) return
    setViewYear(anchor.getFullYear())
    setViewMonth(anchor.getMonth())
  }, [isOpen, anchor])

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setPosition({
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
      width: rect.width
    })
  }, [])

  useEffect(() => {
    if (!isOpen) return
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen, updatePosition])

  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return
      }
      setIsOpen(false)
    }
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEsc)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEsc)
    }
  }, [isOpen])

  // Navigation handlers
  const shiftMonth = useCallback(
    (delta: number) => {
      const next = new Date(viewYear, viewMonth + delta, 1)
      setViewYear(next.getFullYear())
      setViewMonth(next.getMonth())
    },
    [viewYear, viewMonth]
  )

  const shiftYear = useCallback((delta: number) => {
    setViewYear(y => y + delta)
  }, [])

  const handleDateClick = useCallback(
    (date: Date) => {
      if (!isDateInSelectableRange(date, min, max)) return
      onChange(toApiDateString(date))
      setIsOpen(false)
    },
    [min, max, onChange]
  )

  const handleClear = useCallback(() => {
    onChange('')
    setIsOpen(false)
  }, [onChange])

  const todayInRange = isDateInSelectableRange(new Date(), min, max)

  const handleToday = useCallback(() => {
    const today = new Date()
    if (!isDateInSelectableRange(today, min, max)) return
    const todayYmd = toApiDateString(today)
    onChange(clampDateString(todayYmd, min, max))
    setIsOpen(false)
  }, [min, max, onChange])

  const handleToggle = () => {
    if (isDisabled) return
    setIsOpen(open => !open)
  }

  const displayLabel = isValidYmd(value) ? formatDisplayDate(value) : placeholder

  return (
    <>
      <button
        ref={triggerRef}
        type='button'
        aria-label={ariaLabel}
        aria-haspopup='dialog'
        aria-expanded={isOpen}
        data-testid={testId}
        disabled={isDisabled}
        onClick={handleToggle}
        className={cm(
          'h-10 w-full inline-flex items-center justify-between gap-2 rounded-[12px] border border-tokens-neutral-border-1 bg-white px-3 text-sm transition-colors select-none',
          !isDisabled &&
            'hover:border-tokens-status-link-1 focus:outline-none focus:border-tokens-status-link-1',
          isDisabled && 'cursor-not-allowed bg-tokens-neutral-fill-fill-6 opacity-60',
          className
        )}
      >
        <span
          className={cm(
            'truncate text-left',
            isValidYmd(value)
              ? 'text-tokens-neutral-text-primary'
              : 'text-tokens-neutral-text-placeholder'
          )}
          style={{ fontFamily: 'Geist, sans-serif' }}
        >
          {displayLabel}
        </span>
        <CalendarIcon className='size-5 text-tokens-neutral-text-quaternary flex-shrink-0' />
      </button>

      {isOpen &&
        typeof window !== 'undefined' &&
        createPortal(
          <div
            ref={popoverRef}
            role='dialog'
            aria-label='Date picker'
            className='bg-white rounded-[16px] overflow-hidden'
            style={{
              position: 'absolute',
              top: position.top,
              left: position.left,
              minWidth: Math.max(position.width, isCompactViewport ? 0 : 296),
              maxWidth: 'calc(100vw - 24px)',
              zIndex: 9999,
              boxShadow:
                '0px 6px 16px 0px rgba(0,0,0,0.08), 0px 3px 6px -4px rgba(0,0,0,0.12), 0px 9px 28px 8px rgba(0,0,0,0.05)'
            }}
          >
            <MonthCalendar
              year={viewYear}
              month={viewMonth}
              selectedStart={selectedDate}
              minSelectableDate={min}
              maxSelectableDate={max}
              onDateClick={handleDateClick}
              onPrevMonth={() => shiftMonth(-1)}
              onNextMonth={() => shiftMonth(1)}
              onPrevYear={isCompactViewport ? undefined : () => shiftYear(-1)}
              onNextYear={isCompactViewport ? undefined : () => shiftYear(1)}
              compact={isCompactViewport}
            />

            {(allowClear || allowToday) && (
              <div className='flex items-center justify-between px-4 py-3 border-t border-tokens-neutral-fill-fill-6'>
                {allowClear ? (
                  <button
                    type='button'
                    onClick={handleClear}
                    disabled={!isValidYmd(value)}
                    className={cm(
                      'h-7 px-2 rounded-md text-sm transition-colors select-none',
                      isValidYmd(value)
                        ? 'text-tokens-status-link-1 hover:bg-tokens-neutral-fill-fill-4 cursor-pointer'
                        : 'text-tokens-neutral-text-quaternary cursor-not-allowed'
                    )}
                    data-testid='date-picker-clear'
                  >
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyMd'
                      fontWeight='medium'
                      style={{ fontSize: '14px', lineHeight: '22px' }}
                    >
                      Clear
                    </Typography>
                  </button>
                ) : (
                  <span />
                )}

                {allowToday ? (
                  <button
                    type='button'
                    onClick={handleToday}
                    disabled={!todayInRange}
                    className={cm(
                      'h-7 px-2 rounded-md text-sm transition-colors select-none',
                      todayInRange
                        ? 'text-tokens-status-link-1 hover:bg-tokens-neutral-fill-fill-4 cursor-pointer'
                        : 'text-tokens-neutral-text-quaternary cursor-not-allowed'
                    )}
                    data-testid='date-picker-today'
                  >
                    <Typography
                      fontFamily='geist'
                      fontSize='bodyMd'
                      fontWeight='medium'
                      style={{ fontSize: '14px', lineHeight: '22px' }}
                    >
                      Today
                    </Typography>
                  </button>
                ) : (
                  <span />
                )}
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  )
}

export default DatePickerPopover
